<template>
    <section class="auth-box">
        <v-card class='tab-container'>
            <v-tabs v-model="tab" fixedTabs>
                <v-tab data-cy="scan-tab">Scan</v-tab>
                <v-tab data-cy="login-tab">Log In</v-tab>

                <v-tab-item>
                    <v-card class="scan-card" elevation="0">
                        <v-card-text>
                            <v-text-field
                                v-model.trim="models.scan"
                                autofocus
                                dense
                                outlined
                                filled
                                clearable
                                label="Scan ID"
                                @keydown.enter.native="submitButtonHandler()"
                            />
                        </v-card-text>
                    </v-card>
                </v-tab-item>

                <v-tab-item>
                    <v-card class="login-card" elevation="0">
                        <v-card-text>
                            <v-text-field
                                v-model.trim="models.username"
                                dense
                                outlined
                                filled
                                type="email"
                                label="Email"
                                data-cy="input-email"
                                @keydown.enter.native="submitButtonHandler()"
                            />
                            <v-text-field
                                v-model.trim="models.password"
                                dense
                                outlined
                                filled
                                :type="models.showPassword ? 'text' : 'password'"
                                label="Password"
                                :appendIcon="models.showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                data-cy="input-password"
                                @click:append="models.showPassword = !models.showPassword"
                                @keydown.enter.native="submitButtonHandler()"
                            />
                        </v-card-text>
                    </v-card>
                </v-tab-item>

            </v-tabs>
            <v-card-actions>
                <v-spacer />
                <ClButton
                    size="large"
                    buttonText="Login"
                    :rounded="false"
                    :loading="authorizationInFlight"
                    data-cy="submit"
                    @click="submitButtonHandler()"
                />
                <v-spacer />
            </v-card-actions>
        </v-card>
    </section>
</template>

<script>
// Utils
import ValidationUtil from '@/utils/ValidationUtil';

const availableAuthOptions = {
    scan: 'scan',
    login: 'login',
};

export default {
    name: 'TheAuthBox',
    props: {
        /* Query param that is the 'next' page to redirect to upon successful login */
        next: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            tab: null,
            /** @type { String } */
            selectedAuthOption: availableAuthOptions.scan,

            models: {
                /** @type { String } */
                scan: '',
                /** @type { String } */
                username: '',
                /** @type { String } */
                password: '',
                /** @type { String } */
                showPassword: false,
            },

            /** @type { Boolean } */
            authorizationInFlight: false,
        };
    },
    computed: {
        /** @returns { Boolean } */
        selectedOptionIsScan() {
            return this.selectedAuthOption === availableAuthOptions.scan;
        },
        /** @returns { Boolean } */
        selectedOptionIsLogin() {
            return this.selectedAuthOption === availableAuthOptions.login;
        },

        /** @returns { Boolean } */
        hasScanData() {
            return ( this.models.scan.length > 0 );
        },
        /** @returns { Boolean } */
        hasValidScanData() {
            return this.hasScanData
                && this.isValidBase64String( this.models.scan )
                && atob( this.models.scan ).includes( '|' );
        },
        /** @returns { Object } */
        scannedCredentials() {
            return this.hasValidScanData
                ? {
                    username: atob( this.models.scan ).split( '|' )[ 0 ],
                    password: atob( this.models.scan ).split( '|' )[ 2 ],
                }
                : {
                    username: '',
                    password: '',
                };
        },

        /** @returns { Boolean } */
        hasLoginData() {
            return ( this.models.username.length > 0 ) && ( this.models.password.length > 0 );
        },
        /** @returns { Boolean } */
        isReadyToLogin() {
            return this.hasScanData || this.hasLoginData;
        },
    },
    methods: {
        scanButtonHandler() {
            this.selectedAuthOption = availableAuthOptions.scan;
        },
        loginButtonHandler() {
            this.selectedAuthOption = availableAuthOptions.login;
        },
        /** @returns { Boolean } */
        isValidBase64String( paramString ) {
            return ValidationUtil.isValidBase64String( paramString );
        },
        popToast( message, type = 'error' ) {
            this.$store.dispatch( 'snackbar', {
                duration: 5000,
                message,
                type,
            } );
        },
        /** @returns { String } */
        messageFromServer( response ) {
            return response && response.data ? response.data.message : '';
        },
        async authorizeUser( { username, password } ) {
            this.authorizationInFlight = true;
            try {
                await this.$store.dispatch( 'User/login', {
                    username,
                    password,
                } );
                this.$emit( 'authorized' );
            } catch ( { response } ) {
                this.popToast( `Login Failed: ${ this.messageFromServer( response ) || 'Unknown Error' }` );
            } finally {
                this.authorizationInFlight = false;
            }
        },
        submitButtonHandler() {
            if ( this.hasValidScanData ) {
                this.authorizeUser( this.scannedCredentials );
            } else if ( this.hasLoginData ) {
                this.authorizeUser( {
                    username: this.models.username,
                    password: this.models.password,
                } );
            } else {
                this.popToast( 'Scan your ID, or enter your Username and Password to login.', 'info' );
            }
        },
    },
};
</script>

<style scoped lang="scss">
.auth-box {
    .scan-card {
        &.v-card {
            padding-top: 2rem;
            display: flex;
            flex-direction: column;
            align-items: center;

            .v-card__text {
                max-width: 25rem;
            }
        }
    }

    .v-card__actions {
        padding-bottom: 2rem;
    }
}
</style>
